import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dismissNotification } from '../../utils/notificationDismissCookie';
import './FloatingAssistWidget.css';
import './FloatingAssistToast.css';

const DEFAULT_DURATION_MS = 5000;

export interface FloatingAssistToastProps {
  visible: boolean;
  message: string;
  ackLabel?: string;
  dismissCookieKey?: string;
  durationMs?: number;
  onDismiss?: () => void;
}

const FloatingAssistToast: React.FC<FloatingAssistToastProps> = ({
  visible,
  message,
  ackLabel = 'I know',
  dismissCookieKey,
  durationMs = DEFAULT_DURATION_MS,
  onDismiss,
}) => {
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingMsRef = useRef(durationMs);
  const timerStartRef = useRef<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [progressKey, setProgressKey] = useState(0);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const hideToast = useCallback(() => {
    clearDismissTimer();
    timerStartRef.current = null;
    setIsPaused(false);
    setIsOpen(false);
    onDismiss?.();
  }, [clearDismissTimer, onDismiss]);

  const scheduleDismiss = useCallback(() => {
    clearDismissTimer();
    if (remainingMsRef.current <= 0) {
      hideToast();
      return;
    }
    timerStartRef.current = Date.now();
    dismissTimerRef.current = setTimeout(() => {
      hideToast();
    }, remainingMsRef.current);
  }, [clearDismissTimer, hideToast]);

  const pauseDismiss = useCallback(() => {
    if (timerStartRef.current === null) {
      return;
    }
    const elapsed = Date.now() - timerStartRef.current;
    remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
    timerStartRef.current = null;
    clearDismissTimer();
    setIsPaused(true);
  }, [clearDismissTimer]);

  const resumeDismiss = useCallback(() => {
    setIsPaused(false);
    if (remainingMsRef.current <= 0) {
      hideToast();
      return;
    }
    scheduleDismiss();
  }, [hideToast, scheduleDismiss]);

  useEffect(() => {
    if (!visible) {
      setIsOpen(false);
      clearDismissTimer();
      return undefined;
    }

    remainingMsRef.current = durationMs;
    timerStartRef.current = null;
    setIsPaused(false);
    setIsOpen(true);
    setProgressKey((key) => key + 1);
    scheduleDismiss();

    return () => {
      clearDismissTimer();
    };
  }, [visible, durationMs, clearDismissTimer, scheduleDismiss]);

  const handleAck = useCallback(() => {
    if (dismissCookieKey) {
      dismissNotification(dismissCookieKey);
    }
    hideToast();
  }, [dismissCookieKey, hideToast]);

  if (!visible || !isOpen) {
    return null;
  }

  const toastClassName = [
    'floating-assist',
    'floating-assist--hint-left',
    'floating-assist--active',
    'floating-assist--drawer-open',
    'floating-assist-toast',
    isPaused && 'floating-assist-toast--paused',
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    '--toast-duration': `${durationMs}ms`,
  } as React.CSSProperties;

  return (
    <div
      className="floating-assist-toast-root"
      role="status"
      aria-live="polite"
      style={rootStyle}
      onMouseEnter={pauseDismiss}
      onMouseLeave={resumeDismiss}
    >
      <div className={toastClassName}>
        <div className="floating-assist__drawer floating-assist__drawer--open">
          <p className="floating-assist__drawer-text">{message}</p>
          <button type="button" className="floating-assist-toast__ack" onClick={handleAck}>
            {ackLabel}
          </button>
        </div>
        <div className="floating-assist-toast__progress" aria-hidden="true">
          <div key={progressKey} className="floating-assist-toast__progress-bar" />
        </div>
      </div>
    </div>
  );
};

export default FloatingAssistToast;
